import { getDesignerEdgeOrientation } from '../design/designer-commands';

const polygonArea = (geometry = []) => {
  if (!Array.isArray(geometry) || geometry.length < 3) return 0;
  return (
    Math.abs(
      geometry.reduce((area, point, index) => {
        const next = geometry[(index + 1) % geometry.length];
        return area + point[0] * next[1] - next[0] * point[1];
      }, 0)
    ) / 2
  );
};

export function calculateSegmentArea(segment, resolution) {
  const pitchRadians = (Number(segment?.pitch) * Math.PI) / 180;
  const squareCentimeters =
    polygonArea(segment?.geometry) * Number(resolution) ** 2;
  const squareFeet =
    (squareCentimeters * 0.0001 * 10.7639) / Math.cos(pitchRadians);
  return Number.isNaN(squareFeet) ? 0 : squareFeet;
}

export function calculateLegacySegmentArea(segment, samplingRate = 30) {
  const pitch = Math.tan(Number(segment?.pitch) * 0.0174533) * 12;
  const slopeFactor = Math.sqrt(pitch * pitch + 12 * 12) / 12;
  const squareMeters =
    (polygonArea(segment?.geometry) * slopeFactor * samplingRate) / 10000;
  const squareFeet = squareMeters * 10.763910416709722;
  return Number.isNaN(squareFeet) ? 0 : squareFeet;
}

const polygonCentroid = (geometry = []) => {
  if (!Array.isArray(geometry) || geometry.length === 0) return [0, 0];
  let twiceArea = 0;
  let x = 0;
  let y = 0;
  geometry.forEach((point, index) => {
    const previous = geometry[(index + geometry.length - 1) % geometry.length];
    const factor = point[0] * previous[1] - previous[0] * point[1];
    twiceArea += factor;
    x += (point[0] + previous[0]) * factor;
    y += (point[1] + previous[1]) * factor;
  });
  const denominator = twiceArea * 3;
  return denominator ? [x / denominator, y / denominator] : [...geometry[0]];
};

export function rotateSegmentPanels(segment, panelLayout, azimuth) {
  if (!panelLayout || typeof panelLayout !== 'object') return panelLayout;
  const angle = azimuth - panelLayout.azimuth;
  if (Math.abs(angle) < 0.1) return panelLayout;

  const cosine = Math.cos((angle / 180) * Math.PI);
  const sine = Math.sin((angle / 180) * Math.PI);
  const [centerX, centerY] = polygonCentroid(segment?.geometry);
  const rotatePoint = (point) => {
    if (!Array.isArray(point) || point.length < 2) return point;
    const [x, y] = point;
    if (!Number.isFinite(Number(x)) || !Number.isFinite(Number(y))) {
      return point;
    }
    return [
      (x - centerX) * cosine - (y - centerY) * sine + centerX,
      (x - centerX) * sine + (y - centerY) * cosine + centerY
    ];
  };

  return {
    ...panelLayout,
    azimuth,
    vertical: {
      ...panelLayout.vertical,
      points: (panelLayout.vertical?.points || []).map(rotatePoint)
    },
    horizontal: {
      ...panelLayout.horizontal,
      points: (panelLayout.horizontal?.points || []).map(rotatePoint)
    }
  };
}

const pointInPolygon = (point, geometry) => {
  let inside = false;
  for (
    let index = 0, previous = geometry.length - 1;
    index < geometry.length;
    previous = index++
  ) {
    const [x1, y1] = geometry[index];
    const [x2, y2] = geometry[previous];
    if (
      y1 > point[1] !== y2 > point[1] &&
      point[0] < ((x2 - x1) * (point[1] - y1)) / (y2 - y1) + x1
    ) {
      inside = !inside;
    }
  }
  return inside;
};

const edgeIntersection = (edgeA, edgeB) => {
  const denominator =
    (edgeB[1][1] - edgeB[0][1]) * (edgeA[1][0] - edgeA[0][0]) -
    (edgeB[1][0] - edgeB[0][0]) * (edgeA[1][1] - edgeA[0][1]);
  const factor =
    ((edgeB[1][0] - edgeB[0][0]) * (edgeA[0][1] - edgeB[0][1]) -
      (edgeB[1][1] - edgeB[0][1]) * (edgeA[0][0] - edgeB[0][0])) /
    denominator;
  return [
    edgeA[0][0] + factor * (edgeA[1][0] - edgeA[0][0]),
    edgeA[0][1] + factor * (edgeA[1][1] - edgeA[0][1])
  ];
};

const inwardEdgeNormals = (geometry) => {
  const orientation = getDesignerEdgeOrientation(geometry, 0);
  return geometry.map((point, index) => {
    let dx = geometry[(index + 1) % geometry.length][0] - point[0];
    let dy = geometry[(index + 1) % geometry.length][1] - point[1];
    const edgeLength = Math.sqrt(dx * dx + dy * dy);
    if (orientation === 'ccw') {
      dx = -dx;
      dy = -dy;
    }
    return [-dy / edgeLength, dx / edgeLength];
  });
};

const distanceToSegmentSquared = (point, start, end) => {
  const lengthSquared = (start[0] - end[0]) ** 2 + (start[1] - end[1]) ** 2;
  if (lengthSquared === 0) {
    return (point[0] - start[0]) ** 2 + (point[1] - start[1]) ** 2;
  }
  const factor = Math.max(
    0,
    Math.min(
      1,
      ((point[0] - start[0]) * (end[0] - start[0]) +
        (point[1] - start[1]) * (end[1] - start[1])) /
        lengthSquared
    )
  );
  return (
    (point[0] - (start[0] + factor * (end[0] - start[0]))) ** 2 +
    (point[1] - (start[1] + factor * (end[1] - start[1]))) ** 2
  );
};

const nearestLine = (point, geometry) => {
  const distances = geometry.map((start, index) =>
    Math.sqrt(
      distanceToSegmentSquared(
        point,
        start,
        geometry[(index + 1) % geometry.length]
      )
    )
  );
  const minimum = Math.min(...distances);
  return [distances.indexOf(minimum), minimum];
};

export function padSegmentPolygon(shape, scale, setback, mode, resolution) {
  const geometry = Array.isArray(shape?.geometry) ? shape.geometry : [];
  if (!geometry.length) return [];
  const normals = inwardEdgeNormals(geometry);
  const offsetEdges = [];

  if (mode === 0 || mode === 1) {
    geometry.forEach((point, index) => {
      let setbackInches = 0;
      if (shape.lineSetbacks?.[index]) {
        setbackInches = shape.lineSetbacks[index];
      } else if (mode === 0) {
        setbackInches = shape.lines?.[index] === 'Eave' ? 0 : setback;
      } else {
        const label = shape.lines?.[index];
        setbackInches =
          setback && Object.prototype.hasOwnProperty.call(setback, label)
            ? setback[label]
            : 0;
      }
      const distance =
        (Number(setbackInches) * 2.54 * Number(scale)) / Number(resolution);
      const difference = [
        normals[index][0] * distance,
        normals[index][1] * distance
      ];
      const next = geometry[(index + 1) % geometry.length];
      offsetEdges.push([
        [point[0] + difference[0], point[1] + difference[1]],
        [next[0] + difference[0], next[1] + difference[1]]
      ]);
    });
  }

  const vertices = offsetEdges.map((edge, index) =>
    edgeIntersection(
      offsetEdges[(index + offsetEdges.length - 1) % offsetEdges.length],
      edge
    )
  );

  vertices.slice().forEach((vertex, index) => {
    if (pointInPolygon(vertex, geometry)) return;
    const [lineIndex, distance] = nearestLine(vertex, geometry);
    if (distance <= 0.5) return;
    const segmentEdge = [
      geometry[lineIndex],
      geometry[(lineIndex + 1) % geometry.length]
    ];
    const previousEdge = [
      vertices[(index + vertices.length - 1) % vertices.length],
      vertices[index]
    ];
    const nextEdge = [vertices[index], vertices[(index + 1) % vertices.length]];
    vertices.splice(
      index,
      1,
      edgeIntersection(segmentEdge, previousEdge),
      edgeIntersection(segmentEdge, nextEdge)
    );
  });

  return vertices;
}
